// ─── Simulação unificada de comissão (originei-simulacao, Rodada 1 / DEV-3) ───
//
// Espelha os records Java de `br.com.originei.simulacao.dto.*` — não renomear campos.
// Unidades: BRL; `spread`, `percentualFaixa`, `aliquota` e `taxaNegociada` chegam e saem
// como FRAÇÃO (0,05 = 5%). A conversão para percentual acontece só na borda da UI
// (`pages/simulacao/fracao.ts`). Nada aqui calcula: os números vêm do backend.

/** `motor.ModeloSimulacao` — nomeia a fórmula; o modelo de cada produto é dado versionado. */
export type ModeloSimulacao =
  | 'RECORRENTE'
  | 'PONTUAL'
  | 'CAMPANHA'
  | 'RECEITA_LIQUIDA_360'
  | 'SEM_FORMULA'

export const MODELOS_SIMULACAO: readonly ModeloSimulacao[] = [
  'RECORRENTE',
  'PONTUAL',
  'CAMPANHA',
  'RECEITA_LIQUIDA_360',
  'SEM_FORMULA',
]

/** Rótulos dos modelos (texto do PDF §4). */
export const ROTULO_MODELO: Record<ModeloSimulacao, string> = {
  RECORRENTE: 'Recorrente (valor × spread × faixa)',
  PONTUAL: 'Pontual (valor × spread × faixa)',
  CAMPANHA: 'Campanha de Incentivo (taxa ÷ 4 × faixa)',
  /** P4: o percentual NÃO é literal — vem da política de comissão vigente (`percentualReceitaLiquida360`). */
  RECEITA_LIQUIDA_360: 'Percentual da receita líquida Originei (sem faixa)',
  SEM_FORMULA: 'Sem fórmula homologada',
}

/** `entity.SimulacaoComissao.Estado`. */
export type EstadoSimulacao = 'CALCULADA' | 'BLOQUEADA_PARCEIRO' | 'BLOQUEADA_POLITICA'

/**
 * `motor.MotivoSemFormula` — por que um produto SEM_FORMULA não simula (Decisões 20/10 §5.1):
 * CAPEX. `REGRA_COMISSIONAMENTO_NAO_APROVADA` era o da Duplicata Escritural, RETIRADA (Políticas
 * Vigentes 25/10, seção 8): fica no tipo só para ler snapshot antigo que o gravou.
 */
export type MotivoSemFormula = 'POLITICA_COMERCIAL_EM_DEFINICAO' | 'REGRA_COMISSIONAMENTO_NAO_APROVADA'

/**
 * Constantes de `service.SimulacaoComissaoService` (motivo do bloqueio). `TRIBUTOS_NAO_VIGENTES`
 * não bloqueia mais (Decisões 20/10 §1.2): é o código da pendência do líquido em `pendencias`.
 */
export type MotivoBloqueio =
  | 'RESERVA_NAO_SINCRONIZADA'
  | 'PARCEIRO_NAO_DEFINIDO'
  | 'RESERVA_ENCERRADA'
  | 'OUTRA_NECESSIDADE'
  | 'POLITICA_PRODUTO_AUSENTE'
  | 'SEM_FORMULA'
  | 'SPREAD_NAO_VIGENTE'
  | 'TRIBUTOS_NAO_VIGENTES'
  /** Produto RETIRADO do catálogo (Políticas Vigentes 25/10, seção 8 — Duplicata Escritural; V9). */
  | 'PRODUTO_RETIRADO'
  | MotivoSemFormula
  /** P4 — ver a seção P4 no fim do arquivo. */
  | MotivoBloqueioP4

/**
 * Códigos de erro 400 da simulação (Onda P0, Políticas Vigentes 25/10). `DATA_REFERENCIA_INVALIDA`
 * (data futura ou anterior à criação da reserva) vem de `/comissao`; os demais vêm do alias legado
 * `/v1/simulacoes/campanha` quando a reserva não admite a Campanha.
 */
export type CodigoErroSimulacao =
  | 'DATA_REFERENCIA_INVALIDA'
  | 'CAMPANHA_NAO_APLICAVEL'
  | 'PRODUTO_RETIRADO'
  | 'POLITICA_COMERCIAL_EM_DEFINICAO'
  | 'OUTRA_NECESSIDADE'
  | 'POLITICA_PRODUTO_AUSENTE'
  | 'RESERVA_ENCERRADA'
  /** P4 — ver a seção P4 no fim do arquivo. */
  | CodigoErroSimulacaoP4

/** Códigos de alerta que acompanham uma simulação CALCULADA sem bloqueá-la. */
export const ALERTA_CAMPANHA_NAO_APLICAVEL = 'CAMPANHA_NAO_APLICAVEL'

/** Situação do líquido de uma simulação CALCULADA (§1.2): sem alíquota vigente, PENDENTE. */
export type SituacaoLiquido = 'CALCULADO' | 'PENDENTE_CONFIGURACAO_TRIBUTARIA'

/** `dto.SimulacaoComissaoRequest` — `POST /v1/simulacoes/comissao`. */
export interface SimulacaoComissaoRequest {
  /** Obrigatório para todo modelo por produto; só a Campanha simula sem reserva. */
  oportunidadeId?: string | null
  /** Só aceito como `CAMPANHA`. */
  modelo?: 'CAMPANHA' | null
  /**
   * ≥ 0 — zero é válido e zera os resultados sem faixa (SIM-09). P4 (Q72): opcional (nulo) só no
   * modelo 360; nos demais o backend responde 400 `VALOR_OPERACAO_OBRIGATORIO`.
   */
  valorOperacao: number | null
  /** Só Campanha. FRAÇÃO (0,05 = 5%). */
  taxaNegociada?: number | null
  /** Só RECEITA_LIQUIDA_360. */
  receitaLiquidaOriginei?: number | null
  /** ISO `YYYY-MM-DD`; resolve as vigências. Ausente = hoje (Decisões 20/10 §6.1). */
  dataReferencia?: string | null
  /** Dados para os ALERTAS de elegibilidade (§4) — nunca bloqueiam. */
  dadosElegibilidade?: DadosElegibilidade | null
}

/** `SimulacaoComissaoRequest.DadosElegibilidade`. */
export interface DadosElegibilidade {
  faturamentoAnual?: number | null
  /**
   * Home Equity (B5): o servidor calcula o LTV com estes dois e compara EXATO (`solicitado <= ltvMax × imóvel`),
   * como na reserva. BRL.
   */
  valorImovel?: number | null
  valorSolicitado?: number | null
  /** LEGADO — FRAÇÃO (0,45 = 45%); ignorado pelo servidor quando imóvel e solicitado vêm. A tela não envia mais. */
  ltv?: number | null
}

/** Parte do resultado indisponível: o que falta e quem corrige (§1.4). */
export interface PendenciaSimulacao {
  codigo: string
  mensagem: string
  responsavel: string
}

/**
 * Alerta de uma simulação calculada — não bloqueia o cálculo. Elegibilidade (§4) ou, desde a Onda P0,
 * `CAMPANHA_NAO_APLICAVEL` (Campanha pedida com reserva cujo produto segue outra fórmula).
 */
export interface AlertaElegibilidade {
  codigo: string
  mensagem: string
  versaoPolitica: string | null
}

/** `SimulacaoComissaoResponse.TributoResponse` — `aliquota` como fração. */
export interface TributoSimulado {
  nome: string
  descricao?: string | null
  aliquota: number
  valor: number
  versao: string
}

/** `dto.SimulacaoComissaoResponse`. Nos estados bloqueados os numéricos vêm nulos. Campos P4 no fim do arquivo. */
export interface SimulacaoComissaoResponse extends CamposRespostaSimulacaoP4 {
  /** Nulo quando nada foi gravado (`RESERVA_NAO_SINCRONIZADA`). */
  id: string | null
  estado: EstadoSimulacao
  motivoBloqueio: MotivoBloqueio | null
  mensagemBloqueio: string | null
  modelo: ModeloSimulacao | null
  natureza: string | null
  oportunidadeId: string | null
  produtoId: string | null
  produtoNome: string | null
  parceiroId: string | null
  parceiroNome: string | null
  valorOperacao: number | null
  /** Fração; nulo para Campanha e 360. */
  spread: number | null
  /** Rótulo humano ("Faixa 2"); nulo quando não há faixa (valor zero, 360). */
  faixa: string | null
  /** Fração. */
  percentualFaixa: number | null
  receitaOriginei: number | null
  comissaoBruta: number | null
  tributos: TributoSimulado[] | null
  valorLiquido: number | null
  versaoPolitica: string | null
  versaoTributos: string | null
  versaoFaixas: string | null
  versaoRegra: string | null
  indicativa: boolean
  aviso: string
  memoriaCalculo: string | null
  criadoEm: string | null
  // ── Decisões 20/10/2026 ──
  /** Versão do spread usado; nula sem spread (Campanha, 360). */
  versaoSpread?: string | null
  /** Quem corrige o bloqueio (§6.2); nulo quando calculada. */
  responsavelCorrecao?: string | null
  /** Só em CALCULADA; nula em simulações anteriores a 20/10. */
  situacaoLiquido?: SituacaoLiquido | null
  pendencias?: PendenciaSimulacao[] | null
  alertas?: AlertaElegibilidade[] | null
  versaoElegibilidade?: string | null
  dataReferencia?: string | null
  /** O snapshot já gravado de um reenvio idempotente. */
  reprocessamento?: boolean
}

// ─── Políticas versionadas (D2/D5) — `/v1/simulacoes/politicas/**` ───

/** `dto.PoliticaProdutoVigenteResponse` — leitura CONSULTOR+ para "Regras por produto". */
export interface PoliticaProdutoVigente {
  produtoId: string
  produtoNome: string
  modelo: ModeloSimulacao
  natureza: string | null
  versao: string
  vigenciaInicio: string
  origem: string
  /** Só com SEM_FORMULA (§5.1). */
  motivoSemFormula?: MotivoSemFormula | null
}

/** `dto.PoliticaProdutoRequest` (ADMIN). */
export interface PoliticaProdutoRequest {
  produtoId: string
  produtoNome: string
  /**
   * P1 (AN-PP1 §2.4): código do catálogo; divergente do projetado → 422
   * `PRODUTO_CODIGO_DIVERGENTE`. Omitido quando o catálogo ainda não traz código.
   */
  produtoCodigo?: string | null
  modelo: ModeloSimulacao
  natureza?: string | null
  /** ISO `YYYY-MM-DD`; nulo = hoje. */
  vigenciaInicio?: string | null
  origem: string
  /** Só com SEM_FORMULA (§5.1). */
  motivoSemFormula?: MotivoSemFormula | null
}

/**
 * P6 (AN-PP6 §3, simulação V14) — publicação com dupla aprovação, comum às quatro `Politica*Response`.
 * `statusPublicacao`: PENDENTE_APROVACAO (proposta; não vale no cálculo) → ATIVA | REJEITADA → ENCERRADA.
 * `legado`: ATIVA anterior à dupla aprovação (sem `aprovadoPor`). Pessoas são o `sub` (UUID).
 * `versaoRegistro`: concorrência otimista — vai no corpo de aprovar/rejeitar (409 `CONFLITO_VERSAO`).
 */
export type StatusPublicacaoPolitica = 'RASCUNHO' | 'PENDENTE_APROVACAO' | 'ATIVA' | 'REJEITADA' | 'ENCERRADA'

export interface PublicacaoPoliticaSimulacao {
  statusPublicacao?: StatusPublicacaoPolitica | string | null
  /** Área dona (`AreaDona`): COMERCIAL | FINANCEIRO | OPERACOES | JURIDICO. */
  owner?: string | null
  propostoPor?: string | null
  aprovadoPor?: string | null
  aprovadoEm?: string | null
  rejeitadoPor?: string | null
  rejeitadoEm?: string | null
  motivoRejeicao?: string | null
  legado?: boolean
  versaoRegistro?: number | null
}

/** Segmento da rota de aprovar/rejeitar (`/v1/simulacoes/politicas/{tipo}/{id}/aprovar|rejeitar`). */
export type TipoPoliticaSimulacao = 'produtos' | 'comissao' | 'spreads' | 'tributos'

/** `dto.PoliticaProdutoResponse`. */
export interface PoliticaProdutoResponse extends PublicacaoPoliticaSimulacao {
  id: string
  produtoId: string
  produtoNome: string
  modelo: ModeloSimulacao
  natureza: string | null
  versao: string
  vigenciaInicio: string
  vigenciaFim: string | null
  /** Vale hoje (vigência semiaberta [início, fim)). */
  vigente: boolean
  origem: string
  criadoPor: string | null
  criadoEm: string
}

/** `dto.PoliticaSpreadRequest` (ADMIN) — `spread` como FRAÇÃO. */
export interface PoliticaSpreadRequest {
  parceiroId: string
  parceiroNome?: string | null
  produtoId: string
  spread: number
  vigenciaInicio: string
  vigenciaFim?: string | null
  origem: string
}

/** `dto.PoliticaSpreadResponse`. */
export interface PoliticaSpreadResponse extends PublicacaoPoliticaSimulacao {
  id: string
  parceiroId: string
  parceiroNome: string | null
  produtoId: string
  /** Fração. */
  spread: number
  versao: string
  vigenciaInicio: string
  vigenciaFim: string | null
  vigente: boolean
  origem: string
  criadoPor: string | null
  criadoEm: string
}

/** `entity.PoliticaTributaria.Nome` / `.Base`. */
export type NomeTributo = 'CSLL' | 'IR' | 'OUTRO'
export type BaseTributo = 'COMISSAO_BRUTA'

/** `entity.PoliticaTributaria.Natureza`. */
export type NaturezaTributo = 'RETENCAO_CONSULTOR'
export const ROTULO_NATUREZA_TRIBUTO: Record<NaturezaTributo, string> = {
  RETENCAO_CONSULTOR: 'Retenção do consultor (indicativa)',
}

export const NOMES_TRIBUTO: readonly NomeTributo[] = ['CSLL', 'IR', 'OUTRO']
export const ROTULO_BASE_TRIBUTO: Record<BaseTributo, string> = {
  COMISSAO_BRUTA: 'Comissão bruta',
}

/** `dto.PoliticaTributariaRequest` (ADMIN) — `aliquota` como FRAÇÃO. */
export interface PoliticaTributariaRequest {
  nome: NomeTributo
  descricao?: string | null
  aliquota: number
  base: BaseTributo
  vigenciaInicio: string
  vigenciaFim?: string | null
  origem: string
}

/** `dto.PoliticaTributariaResponse`. */
export interface PoliticaTributariaResponse extends PublicacaoPoliticaSimulacao {
  id: string
  nome: NomeTributo
  descricao: string | null
  /** Fração. */
  aliquota: number
  base: BaseTributo
  versao: string
  vigenciaInicio: string
  vigenciaFim: string | null
  vigente: boolean
  origem: string
  criadoPor: string | null
  criadoEm: string
  /** `PoliticaTributaria.Natureza` — hoje só `RETENCAO_CONSULTOR` (retenção indicativa do consultor, Q73). */
  natureza?: NaturezaTributo | string | null
}

// ─── P4 (AN-PP4 §2.5) — política de comissão GLOBAL e versionada ───────────────────────────
//
// Conferido com o B1 real na integração (`dto.PoliticaComissaoResponse`/`PoliticaComissaoRequest`).
// Faixas e o percentual do 360 são DADO do backend: nenhum número fixo na tela. Percentuais
// trafegam como FRAÇÃO (0,2 = 20%); `valorAte` em BRL.

/** Uma faixa por valor da operação, em ordem. Última faixa sem teto (`valorAte` nulo). */
export interface FaixaComissao {
  ordem: number
  /** "Faixa 1"… (gerado pelo backend). */
  rotulo: string
  /** BRL; nulo = sem teto (só a última). */
  valorAte: number | null
  /** `true` = o limite pertence à faixa (≤); `false` = estritamente abaixo (<). */
  ateInclusivo: boolean
  /** Fração. */
  percentual: number
}

/** `dto.PoliticaComissaoResponse` — `GET /comissao` (ADMIN) e `GET /comissao/vigente` (CONSULTOR+). */
export interface PoliticaComissaoResponse extends PublicacaoPoliticaSimulacao {
  id: string
  /** `YYYY-MM-DD.vN` (sequência global). */
  versao: string
  /** Rótulo da tabela de faixas gravado no snapshot (`faixas.v1`…). */
  versaoFaixas: string
  /** Fração — vem do backend, nunca literal na tela. */
  percentualReceitaLiquida360: number
  vigenciaInicio: string
  vigenciaFim: string | null
  /** Vale hoje (vigência semiaberta). Suposição: o B1 devolve como as demais políticas. */
  vigente: boolean
  origem: string
  criadoPor: string | null
  criadoEm: string
  faixas: FaixaComissao[]
  /** Texto das faixas gerado pelo backend ("<5MM 20% | 5-10MM incl. 30% | …"). Contrato real do B1. */
  descricaoFaixas?: string | null
}

/** Faixa no `POST /comissao` — `ordem` = posição na lista; `rotulo` é do backend. */
export interface FaixaComissaoRequest {
  ordem: number
  valorAte: number | null
  ateInclusivo: boolean
  /** Fração. */
  percentual: number
}

/** `dto.PoliticaComissaoRequest` (ADMIN_ONLY) — `versao`/`versaoFaixas` automáticas no backend. */
export interface PoliticaComissaoRequest {
  faixas: FaixaComissaoRequest[]
  /** Fração. */
  percentualReceitaLiquida360: number
  /** ISO `YYYY-MM-DD`; não pode ser anterior a hoje (422 `VIGENCIA_INVALIDA`). */
  vigenciaInicio: string
  origem: string
}

/**
 * Recusas 422 do cadastro (`SimulacaoRegraException`: `errorCode` + `message` + `data` com o erro por
 * campo — `faixas[i].valorAte`, `faixas[i].percentual`, `vigenciaInicio`…). Encerrar uma já encerrada:
 * 400 `POLITICA_JA_ENCERRADA`; sem vigente em `/comissao/vigente`: 404 `POLITICA_COMISSAO_AUSENTE`.
 */
export type CodigoErroPoliticaComissao =
  | 'FAIXAS_INVALIDAS'
  | 'VIGENCIA_INVALIDA'
  | 'POLITICA_JA_ENCERRADA'
  | 'POLITICA_COMISSAO_AUSENTE'

// ─── P4 (AN-PP4 §§3, 5, 6, 7) — contrato da RESPOSTA da simulação (DEV-PP4-B2 em paralelo) ────
//
// Conferido com o B2 real na integração (`dto.SimulacaoComissaoResponse`). Campos opcionais porque
// snapshots anteriores à P4 não os trazem.

/** Bloqueio novo da P4: nenhuma política de comissão vigente na data de referência. */
export type MotivoBloqueioP4 = 'POLITICA_COMISSAO_AUSENTE'

/**
 * 400 da P4: `Idempotency-Key` ausente/em branco, chave > 100 caracteres, mesma chave com outra
 * requisição e valor da operação ausente fora do 360.
 */
export type CodigoErroSimulacaoP4 =
  | 'IDEMPOTENCIA_OBRIGATORIA'
  | 'IDEMPOTENCIA_CHAVE_INVALIDA'
  | 'IDEMPOTENCIA_CONFLITO'
  | 'VALOR_OPERACAO_OBRIGATORIO'

/** `correctionOwner` do PDF — quem corrige um bloqueio. */
export type ResponsavelCorrecao = 'ORIGINEI' | 'ADMINISTRADOR' | 'FINANCEIRO' | 'NENHUM'

/** `MotivoBloqueioResponse` — um item de `motivosBloqueio[]` (o primeiro é o principal). */
export interface MotivoBloqueioDetalhe {
  codigo: MotivoBloqueio | string
  mensagem: string | null
  responsavel?: ResponsavelCorrecao | null
  /** Texto de hoje ("Financeiro (spread do parceiro)"). */
  responsavelCorrecao?: string | null
  /** `requiredAction`. */
  acaoRequerida?: string | null
}

/** Unidade do valor de um passo: BRL (já arredondado pelo backend) ou FRAÇÃO (sem arredondar). */
export type UnidadePasso = 'BRL' | 'FRACAO'

/** `PassoCalculoResponse` — fórmula explicável estruturada; a tela só exibe. */
export interface PassoCalculo {
  ordem: number
  /** `RECEITA_ORIGINEI`, `TAXA_ORIGINEI`, `FAIXA`, `COMISSAO_BRUTA`, `RETENCAO_<n>`, `VALOR_LIQUIDO`. */
  codigo: string
  rotulo: string
  formula: string | null
  /** Nulo: faixa no valor zero, líquido pendente de configuração tributária. */
  valor: number | null
  unidade: UnidadePasso
}

/** `commissionModel` do PDF (tradução de `ModeloSimulacao`). */
export type ModeloComissao =
  | 'VOLUME_BAND_RECURRING'
  | 'VOLUME_BAND_ONE_OFF'
  | 'INCENTIVE_QUARTER_RATE_BAND'
  | 'NET_ORIGINEI_REVENUE_15'

/** `motor.ModeloComissao.Recorrencia`. */
export type RecorrenciaComissao = 'RECORRENTE' | 'UNICA'

export type TipoPoliticaAplicada =
  | 'PRODUTO'
  | 'COMISSAO'
  | 'SPREAD'
  | 'ELEGIBILIDADE'
  | 'TRIBUTO'
  | 'CHECKLIST'
  | 'CATALOGO'

/** Uma política efetiva do snapshot, com a vigência do MOMENTO da simulação (nula em linha antiga). */
export interface PoliticaAplicada {
  tipo: TipoPoliticaAplicada | string
  id?: string | null
  versao: string | null
  vigenciaInicio?: string | null
  vigenciaFim?: string | null
  origem?: string | null
  /** COMISSAO → versão das faixas; TRIBUTO → nome[/descrição]; CATALOGO → código do produto. */
  detalhe?: string | null
}

/** Campos novos no FIM do `SimulacaoComissaoResponse` (nada renomeado; os antigos continuam). */
export interface CamposRespostaSimulacaoP4 {
  /** Vazio quando CALCULADA; `[0]` coincide com `motivoBloqueio`/`mensagemBloqueio`/`responsavelCorrecao`. */
  motivosBloqueio?: MotivoBloqueioDetalhe[] | null
  /** Vazio em snapshot anterior à P4 (a tela cai para `memoriaCalculo`). */
  passos?: PassoCalculo[] | null
  modeloComissao?: ModeloComissao | null
  /** `ModeloComissao.Recorrencia`; nula quando `modeloComissao` é nulo. */
  recorrencia?: RecorrenciaComissao | null
  politicaComissaoId?: string | null
  versaoPoliticaComissao?: string | null
  politicasAplicadas?: PoliticaAplicada[] | null
  produtoCodigo?: string | null
  versaoCatalogo?: string | null
  /** Checklist aplicado (`dto.SimulacaoComissaoResponse.checklistId`/`checklistVersao`) — nulos até a P3. */
  checklistId?: string | null
  checklistVersao?: number | null
}
